import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import FormFieldsCompact from '../form/fieldsCompact';
import fields from '../../lib/fields';
import { marketplaceCategoryLabel } from '../../lib/marketplaceCategories';
import { buildFinnBookmarklet } from '../../lib/finnBookmarklet';
import {
    loadFinnQueue, saveFinnQueue, removeFromFinnQueue, clearFinnQueue,
} from '../../lib/finnImportQueue';

// Spreadsheet import of marketplace listings (admins and partners).
// Steps: upload -> preview (select rows) -> importing (poll) -> done.
// Backend: /api/articles/marketplace/import/preview, /import (202 + batchId), GET /import?batchId=.

const POLL_MS = 2000;
// Feed list refresh after «Kjør nå»: every 3 s, give up after a minute.
const FEED_POLL_MS = 3000;
const FEED_MAX_POLLS = 20;
// Polls with no change in «done» before the UI gives up on a run.
const MAX_STALLED_POLLS = 15;
// Unchanged rows can be ticked too (a re-save fixes an old listing's missing fields);
// only new and changed rows are ticked by default.
const SELECTABLE_STATUSES = ['new', 'update', 'unchanged'];
const DEFAULT_SELECTED_STATUSES = ['new', 'update'];

const STATUS_BADGE = {
    new: { cls: 'text-bg-success', label: <Text id='marketplace.import-status-new'>New</Text> },
    update: { cls: 'text-bg-primary', label: <Text id='marketplace.import-status-update'>Update</Text> },
    unchanged: { cls: 'text-bg-secondary', label: <Text id='marketplace.import-status-unchanged'>Unchanged</Text> },
    duplicateInFile: { cls: 'text-bg-warning', label: <Text id='marketplace.import-status-duplicate'>Repeated in file</Text> },
    invalid: { cls: 'text-bg-danger', label: <Text id='marketplace.import-status-invalid'>Invalid</Text> },
};

function fileToBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (ev) => {
            const bytes = new Uint8Array(ev.target.result);
            let binary = '';
            for (let i = 0; i < bytes.byteLength; i += 1) {
                binary += String.fromCharCode(bytes[i]);
            }
            resolve(btoa(binary));
        };
        reader.onerror = () => reject(new Error('Kunne ikke lese filen.'));
        reader.readAsArrayBuffer(file);
    });
}

function isSelectable(item) {
    return SELECTABLE_STATUSES.includes(item.status);
}

function userLabel(user) {
    if (!user) {
        return '';
    }
    return `${user.firstname || ''} ${user.lastname || ''}`.trim() || user.email || `#${user.id}`;
}

const FEED_STATUS_BADGE = {
    ok: 'text-bg-success',
    unchanged: 'text-bg-secondary',
    error: 'text-bg-danger',
};

// User search with arrow-key navigation, used to pick the seller of a feed.
class SellerSearch extends Component {
    constructor(props) {
        super(props);
        this.state = {
            search: '', results: [], searching: false, activeIndex: 0,
        };
        this.timer = null;
    }

    componentWillUnmount() {
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }
    }

    onInput = (e) => {
        const search = e.target.value;
        this.setState({ search });
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }
        if (search.trim().length < 2) {
            this.setState({ results: [], searching: false, activeIndex: 0 });
            return;
        }
        this.setState({ searching: true });
        this.timer = setTimeout(async () => {
            this.timer = null;
            const response = await util.fetchApi('/api/users/search-all', { method: 'GET' }, { search: search.trim() });
            this.setState({
                results: response?.status === 200 && Array.isArray(response.data) ? response.data.slice(0, 8) : [],
                searching: false,
                activeIndex: 0,
            });
        }, 400);
    }

    onKeyDown = (e) => {
        const { results, activeIndex } = this.state;
        if (e.key === 'Escape') {
            this.setState({ results: [], searching: false, activeIndex: 0 });
            return;
        }
        if (results.length === 0) {
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            this.setState({ activeIndex: (activeIndex + 1) % results.length });
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            this.setState({ activeIndex: (activeIndex - 1 + results.length) % results.length });
        } else if (e.key === 'Enter') {
            e.preventDefault();
            this.pick(results[activeIndex] || results[0]);
        }
    }

    pick = (user) => {
        const { onPick = () => {} } = this.props;
        this.setState({
            search: '', results: [], searching: false, activeIndex: 0,
        });
        onPick(user);
    }

    onClickResult = (e) => {
        const id = parseInt(e.currentTarget.dataset.id, 10);
        const user = this.state.results.find(candidate => candidate.id === id);
        if (user) {
            this.pick(user);
        }
    }

    onHover = (e) => {
        const index = parseInt(e.currentTarget.dataset.index, 10);
        if (index >= 0 && index !== this.state.activeIndex) {
            this.setState({ activeIndex: index });
        }
    }

    render() {
        const { placeholder = 'Søk etter bruker (navn, e-post, telefon)' } = this.props;
        const {
            search, results, searching, activeIndex,
        } = this.state;
        return (
            <div class='position-relative'>
                <input
                    type='search'
                    class='form-control form-control-sm'
                    placeholder={placeholder}
                    value={search}
                    onInput={this.onInput}
                    onKeyDown={this.onKeyDown}
                />
                {(results.length > 0 || searching) && (
                    <div class='position-absolute list-group shadow w-100' style='z-index: 1050; top: 100%;'>
                        {searching && results.length === 0 && (
                            <div class='list-group-item small text-body-secondary'>
                                <i class='fa-duotone fa-spinner fa-spin me-1' /> Søker...
                            </div>
                        )}
                        {results.map((candidate, index) => (
                            <button
                                type='button'
                                key={candidate.id}
                                class={`list-group-item list-group-item-action py-1 ${index === activeIndex ? 'active' : ''}`}
                                data-id={candidate.id}
                                data-index={index}
                                onClick={this.onClickResult}
                                onMouseEnter={this.onHover}
                            >
                                <strong class='small'>{userLabel(candidate)}</strong>
                                {candidate.email && <small class='text-body-secondary ms-2'>{candidate.email}</small>}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        );
    }
}

// Queue entries carry no row numbers; the preview endpoint wants 1..n.
function queueRows(queue) {
    return queue.map((ad, index) => ({ ...ad, rowNumber: index + 1 }));
}

@observer
class MarketplaceImportDrawer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            step: 'upload',
            fileName: '',
            loading: false,
            error: '',
            preview: null,
            selected: {},
            batchId: '',
            total: 0,
            done: 0,
            stalledPolls: 0,
            // Admin only: import on behalf of another user (the company that owns the ads).
            seller: null,
            sellerSearch: '',
            sellerResults: [],
            sellerSearching: false,
            sellerActiveIndex: 0,
            bookmarkletCopied: false,
            // Finn ads collected by the bookmarklet, remembered in this browser (lib/finnImportQueue.js).
            queue: loadFinnQueue(),
            // True while the current preview/import comes from that queue.
            queueMode: false,
            importedItems: [],
            // Automatic sources («Automatiske kilder»): scheduled page imports.
            feeds: [],
            feedsLoaded: false,
            feedFormOpen: false,
            feedSeller: null,
            feedSaving: false,
            feedError: '',
            feedBusyId: 0,
            feedConfirmDeleteId: 0,
            // Feed being edited in the form (0 = the form creates a new one).
            feedEditId: 0,
        };
        this.pollTimer = null;
        this.sellerSearchTimer = null;
        this.feedPollTimer = null;
        this.feedPolls = 0;
        this.feedSections = fields.get('marketplaceFeed');
    }

    canManageFeeds = () => {
        const { userStore } = this.props.stores;
        return !!(userStore.isAdmin || userStore.isPartner);
    }

    loadFeeds = async () => {
        if (!this.canManageFeeds()) {
            return [];
        }
        const response = await util.fetchApi('/api/marketplacefeeds/', { method: 'GET' }, {});
        const feeds = response?.status === 200 && Array.isArray(response.data) ? response.data : [];
        this.setState({ feeds, feedsLoaded: true });
        return feeds;
    }

    stopFeedPolling = () => {
        if (this.feedPollTimer) {
            clearTimeout(this.feedPollTimer);
            this.feedPollTimer = null;
        }
    }

    // After «Kjør nå»/create: re-read the list until the feed's lastRunDate moves.
    pollFeeds = (feedId, previousRunDate) => {
        this.stopFeedPolling();
        this.feedPolls = 0;
        const tick = async () => {
            this.feedPollTimer = null;
            this.feedPolls += 1;
            const feeds = await this.loadFeeds();
            const feed = feeds.find(entry => entry.id === feedId);
            const finished = !feed || (feed.lastRunDate && feed.lastRunDate !== previousRunDate && !feed.runStartedDate);
            if (finished || this.feedPolls >= FEED_MAX_POLLS) {
                this.setState({ feedBusyId: 0 });
                const { callback = () => {} } = this.props;
                callback();
                return;
            }
            this.feedPollTimer = setTimeout(tick, FEED_POLL_MS);
        };
        this.feedPollTimer = setTimeout(tick, FEED_POLL_MS);
    }

    toggleFeedForm = () => {
        const { marketplaceFeedStore } = this.props.stores;
        const { feedFormOpen } = this.state;
        if (!feedFormOpen) {
            marketplaceFeedStore.updateKeyValue('newMarketplaceFeed', {});
        }
        this.setState({
            feedFormOpen: !feedFormOpen, feedEditId: 0, feedSeller: null, feedError: '',
        });
    }

    // Pencil on a row: the same form, prefilled; save sends a PATCH instead of a POST.
    editFeed = (e) => {
        const id = parseInt(e.currentTarget.dataset.id, 10);
        const feed = this.state.feeds.find(entry => entry.id === id);
        if (!feed) {
            return;
        }
        const { marketplaceFeedStore } = this.props.stores;
        marketplaceFeedStore.updateKeyValue('newMarketplaceFeed', {
            name: feed.name || '', url: feed.url || '', intervalHours: feed.intervalHours || 24,
        });
        this.setState({
            feedFormOpen: true,
            feedEditId: id,
            feedSeller: feed.sellerUser > 0 ? { id: feed.sellerUser, firstname: feed.sellerName || `#${feed.sellerUser}` } : null,
            feedError: '',
        });
    }

    pickFeedSeller = (user) => {
        this.setState({ feedSeller: user });
    }

    clearFeedSeller = () => {
        this.setState({ feedSeller: null });
    }

    createFeed = async () => {
        const { marketplaceFeedStore, articleStore } = this.props.stores;
        const { feedSeller, feedEditId } = this.state;
        const draft = marketplaceFeedStore.newMarketplaceFeed || {};
        if (!draft.url || !feedSeller) {
            this.setState({ feedError: 'Lenke og selger må fylles ut.' });
            return;
        }
        this.setState({ feedSaving: true, feedError: '' });
        const body = {
            name: draft.name || '',
            url: draft.url,
            intervalHours: draft.intervalHours || 24,
            sellerUser: feedSeller.id,
        };
        if (feedEditId > 0) {
            const response = await util.fetchApi(`/api/marketplacefeeds/${feedEditId}`, { method: 'PATCH' }, body);
            if (response?.status !== 202) {
                this.setState({ feedSaving: false, feedError: response?.message || 'Kunne ikke lagre kilden.' });
                return;
            }
            marketplaceFeedStore.updateKeyValue('newMarketplaceFeed', {});
            this.setState({
                feedSaving: false, feedFormOpen: false, feedEditId: 0, feedSeller: null,
            });
            this.loadFeeds();
            return;
        }
        const response = await util.fetchApi('/api/marketplacefeeds/', { method: 'POST' }, {
            ...body,
            hostname: articleStore.currentHostname,
        });
        if (response?.status !== 201 || !response.data?.id) {
            this.setState({ feedSaving: false, feedError: response?.message || 'Kunne ikke lagre kilden.' });
            return;
        }
        marketplaceFeedStore.updateKeyValue('newMarketplaceFeed', {});
        this.setState({
            feedSaving: false, feedFormOpen: false, feedSeller: null, feedBusyId: response.data.id,
        });
        await this.loadFeeds();
        this.pollFeeds(response.data.id, null);
    }

    runFeedNow = async (e) => {
        const id = parseInt(e.currentTarget.dataset.id, 10);
        const feed = this.state.feeds.find(entry => entry.id === id);
        this.setState({ feedBusyId: id, feedError: '' });
        const response = await util.fetchApi(`/api/marketplacefeeds/${id}/run`, { method: 'POST' }, {});
        if (response?.status !== 202) {
            this.setState({ feedBusyId: 0, feedError: response?.message || 'Kunne ikke starte kjøringen.' });
            return;
        }
        this.pollFeeds(id, feed ? feed.lastRunDate : null);
    }

    // «Analyser på nytt»: the AI writes a fresh recipe for the page, then the feed runs.
    analyzeFeed = async (e) => {
        const id = parseInt(e.currentTarget.dataset.id, 10);
        const feed = this.state.feeds.find(entry => entry.id === id);
        this.setState({ feedBusyId: id, feedError: '' });
        const response = await util.fetchApi(`/api/marketplacefeeds/${id}/analyze`, { method: 'POST' }, {});
        if (response?.status !== 202) {
            this.setState({ feedBusyId: 0, feedError: response?.message || 'Kunne ikke starte analysen.' });
            return;
        }
        this.pollFeeds(id, feed ? feed.lastRunDate : null);
    }

    toggleFeedActive = async (e) => {
        const id = parseInt(e.currentTarget.dataset.id, 10);
        const feed = this.state.feeds.find(entry => entry.id === id);
        if (!feed) {
            return;
        }
        const response = await util.fetchApi(`/api/marketplacefeeds/${id}`, { method: 'PATCH' }, { active: feed.active ? 0 : 1 });
        if (response?.status !== 202) {
            this.setState({ feedError: response?.message || 'Kunne ikke endre kilden.' });
            return;
        }
        this.loadFeeds();
    }

    askDeleteFeed = (e) => {
        this.setState({ feedConfirmDeleteId: parseInt(e.currentTarget.dataset.id, 10) });
    }

    cancelDeleteFeed = () => {
        this.setState({ feedConfirmDeleteId: 0 });
    }

    deleteFeed = async (e) => {
        const id = parseInt(e.currentTarget.dataset.id, 10);
        const response = await util.fetchApi(`/api/marketplacefeeds/${id}`, { method: 'DELETE' }, {});
        this.setState({ feedConfirmDeleteId: 0 });
        if (response?.status !== 200) {
            this.setState({ feedError: response?.message || 'Kunne ikke slette kilden.' });
            return;
        }
        this.loadFeeds();
    }

    copyBookmarklet = async () => {
        const code = buildFinnBookmarklet(window.location.origin);
        try {
            await navigator.clipboard.writeText(code);
            this.setState({ bookmarkletCopied: true });
            setTimeout(() => this.setState({ bookmarkletCopied: false }), 2500);
        } catch (error) {
            // Clipboard blocked: the field is selectable, the user can copy by hand.
            this.setState({ bookmarkletCopied: false });
        }
    }

    handleSellerSearch = (e) => {
        const sellerSearch = e.target.value;
        this.setState({ sellerSearch });
        if (this.sellerSearchTimer) {
            clearTimeout(this.sellerSearchTimer);
            this.sellerSearchTimer = null;
        }
        if (sellerSearch.trim().length < 2) {
            this.setState({ sellerResults: [], sellerSearching: false, sellerActiveIndex: 0 });
            return;
        }
        this.setState({ sellerSearching: true });
        this.sellerSearchTimer = setTimeout(async () => {
            this.sellerSearchTimer = null;
            const response = await util.fetchApi('/api/users/search-all', { method: 'GET' }, { search: sellerSearch.trim() });
            this.setState({
                sellerResults: response?.status === 200 && Array.isArray(response.data) ? response.data.slice(0, 8) : [],
                sellerSearching: false,
                sellerActiveIndex: 0,
            });
        }, 400);
    }

    // Arrow keys move the highlight through the result list, Enter picks it, Escape closes it.
    handleSellerKeyDown = (e) => {
        const { sellerResults, sellerActiveIndex } = this.state;
        if (e.key === 'Escape') {
            this.setState({ sellerResults: [], sellerSearching: false, sellerActiveIndex: 0 });
            return;
        }
        if (sellerResults.length === 0) {
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            this.setState({ sellerActiveIndex: (sellerActiveIndex + 1) % sellerResults.length });
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            this.setState({ sellerActiveIndex: (sellerActiveIndex - 1 + sellerResults.length) % sellerResults.length });
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const candidate = sellerResults[sellerActiveIndex] || sellerResults[0];
            this.pickSeller(candidate.id);
        }
    }

    hoverSeller = (e) => {
        const index = parseInt(e.currentTarget.dataset.index, 10);
        if (index >= 0 && index !== this.state.sellerActiveIndex) {
            this.setState({ sellerActiveIndex: index });
        }
    }

    pickSeller = (id) => {
        const seller = this.state.sellerResults.find(candidate => candidate.id === id) || null;
        this.setState({
            seller, sellerSearch: '', sellerResults: [], sellerActiveIndex: 0,
        }, this.refreshMatching);
    }

    chooseSeller = (e) => {
        this.pickSeller(parseInt(e.currentTarget.dataset.id, 10));
    }

    clearSeller = () => {
        this.setState({
            seller: null, sellerSearch: '', sellerResults: [], sellerActiveIndex: 0,
        }, this.refreshMatching);
    }

    // Matching (new/update/unchanged) depends on whose earlier imports we compare with,
    // so a seller change re-runs the preview on the already mapped rows.
    refreshMatching = async () => {
        const { preview, seller } = this.state;
        if (!preview || !Array.isArray(preview.items)) {
            return;
        }
        this.setState({ loading: true, error: '' });
        const response = await util.fetchApi('/api/articles/marketplace/import/preview', { method: 'POST' }, {
            items: preview.items,
            sellerUserId: seller ? seller.id : 0,
        });
        if (response?.status !== 200 || !response.data) {
            this.setState({ loading: false, error: response?.message || 'Kunne ikke oppdatere forhåndsvisningen.' });
            return;
        }
        // Keep the original mapping info (source, notes) from the file preview.
        this.applyPreview({ ...response.data, mapping: preview.mapping, hasImageColumn: preview.hasImageColumn });
    }

    componentDidMount() {
        // Opened by the Finn bookmarklet (grid merges the run into the queue first): skip the upload step.
        const { previewQueue } = this.props;
        if (previewQueue && this.state.queue.length > 0) {
            this.previewQueue();
        }
        this.loadFeeds();
    }

    previewQueue = () => {
        const { queue } = this.state;
        if (queue.length === 0) {
            return;
        }
        this.setState({ queueMode: true });
        this.previewRows(queueRows(queue), 'Finn.no');
    }

    clearQueue = () => {
        clearFinnQueue();
        const { queueMode } = this.state;
        this.setState({ queue: [], queueMode: false });
        if (queueMode) {
            this.reset();
        }
    }

    // Trash icon on a preview row: drop that ad from the queue and preview the rest again.
    removeQueued = (e) => {
        const rowNumber = parseInt(e.currentTarget.dataset.row, 10);
        const { preview, queue } = this.state;
        const item = (preview?.items || []).find(candidate => candidate.rowNumber === rowNumber);
        if (!item) {
            return;
        }
        const remaining = removeFromFinnQueue(queue, [item]);
        saveFinnQueue(remaining);
        this.setState({ queue: remaining });
        if (remaining.length === 0) {
            this.reset();
            return;
        }
        this.previewRows(queueRows(remaining), 'Finn.no');
    }

    componentWillUnmount() {
        this.stopPolling();
        this.stopFeedPolling();
        if (this.sellerSearchTimer) {
            clearTimeout(this.sellerSearchTimer);
            this.sellerSearchTimer = null;
        }
    }

    stopPolling = () => {
        if (this.pollTimer) {
            clearTimeout(this.pollTimer);
            this.pollTimer = null;
        }
    }

    applyPreview = (preview) => {
        // Default selection: new and changed rows that have a price.
        const selected = {};
        (preview.items || []).forEach(item => {
            if (DEFAULT_SELECTED_STATUSES.includes(item.status) && item.price !== null && item.price !== undefined) {
                selected[item.rowNumber] = true;
            }
        });
        this.setState({
            loading: false, step: 'preview', preview, selected,
        });
    }

    previewRows = async (items, sourceName) => {
        this.setState({ loading: true, error: '', fileName: sourceName });
        const response = await util.fetchApi('/api/articles/marketplace/import/preview', { method: 'POST' }, { items });
        if (response?.status !== 200 || !response.data) {
            this.setState({ loading: false, error: response?.message || 'Kunne ikke lese annonsene.' });
            return;
        }
        this.applyPreview(response.data);
    }

    handleFile = async (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) {
            return;
        }
        this.setState({ loading: true, error: '', fileName: file.name });
        try {
            const fileBase64 = await fileToBase64(file);
            const response = await util.fetchApi('/api/articles/marketplace/import/preview', { method: 'POST' }, {
                fileBase64,
                filename: file.name,
            });
            if (response?.status !== 200 || !response.data) {
                this.setState({ loading: false, error: response?.message || 'Kunne ikke lese filen.' });
                return;
            }
            this.applyPreview(response.data);
        } catch (error) {
            this.setState({ loading: false, error: error.message || 'Kunne ikke lese filen.' });
        }
    }

    toggleRow = (e) => {
        const rowNumber = parseInt(e.target.dataset.row, 10);
        this.setState(prev => ({ selected: { ...prev.selected, [rowNumber]: !prev.selected[rowNumber] } }));
    }

    setAll = (value) => {
        const { preview } = this.state;
        const selected = {};
        (preview?.items || []).forEach(item => {
            if (isSelectable(item)) {
                selected[item.rowNumber] = value;
            }
        });
        this.setState({ selected });
    }

    selectAll = () => this.setAll(true)

    selectNone = () => this.setAll(false)

    reset = () => {
        this.stopPolling();
        this.setState({
            step: 'upload',
            fileName: '',
            loading: false,
            error: '',
            preview: null,
            selected: {},
            batchId: '',
            total: 0,
            done: 0,
            stalledPolls: 0,
            sellerSearch: '',
            sellerResults: [],
            sellerActiveIndex: 0,
            queueMode: false,
            importedItems: [],
        });
    }

    startImport = async () => {
        const { preview, selected } = this.state;
        const { articleStore } = this.props.stores;
        const items = (preview?.items || []).filter(item => isSelectable(item) && selected[item.rowNumber]);
        if (items.length === 0) {
            return;
        }
        this.setState({ loading: true, error: '' });
        const { seller } = this.state;
        const response = await util.fetchApi('/api/articles/marketplace/import', { method: 'POST' }, {
            hostname: articleStore.currentHostname,
            sellerUserId: seller ? seller.id : 0,
            items: items.map(item => ({
                rowNumber: item.rowNumber,
                sourceId: item.sourceId,
                title: item.title,
                description: item.description,
                price: item.price,
                category: item.category,
                imageUrls: item.imageUrls,
                externalUrl: item.externalUrl,
                isSold: item.isSold,
            })),
        });
        if (response?.status !== 202 || !response.data?.batchId) {
            this.setState({ loading: false, error: response?.message || 'Importen kunne ikke startes.' });
            return;
        }
        this.setState({
            loading: false,
            step: 'importing',
            batchId: response.data.batchId,
            total: response.data.total,
            done: 0,
            stalledPolls: 0,
            importedItems: items,
        });
        this.pollTimer = setTimeout(this.poll, POLL_MS);
    }

    poll = async () => {
        this.pollTimer = null;
        const { batchId, total, done: previousDone, stalledPolls } = this.state;
        const response = await util.fetchApi('/api/articles/marketplace/import', { method: 'GET' }, { batchId });
        if (response?.status !== 200) {
            this.setState({ error: response?.message || 'Mistet kontakten med importen.' });
            this.finish();
            return;
        }
        const done = response.data?.done || 0;
        const stalled = done === previousDone ? stalledPolls + 1 : 0;
        this.setState({ done, stalledPolls: stalled });
        if (done >= total || stalled >= MAX_STALLED_POLLS) {
            this.finish();
            return;
        }
        this.pollTimer = setTimeout(this.poll, POLL_MS);
    }

    finish = () => {
        const { callback = () => {} } = this.props;
        this.stopPolling();
        this.setState({ step: 'done' });
        // Imported ads leave the browser queue; ads that were not ticked stay for next time.
        const { queueMode, queue, importedItems } = this.state;
        if (queueMode) {
            const remaining = removeFromFinnQueue(queue, importedItems);
            saveFinnQueue(remaining);
            this.setState({ queue: remaining, queueMode: false, importedItems: [] });
        }
        callback();
    }

    close = () => {
        const { drawerLevel = 1 } = this.props;
        const { appState } = this.props.stores;
        this.stopPolling();
        appState.closeDrawer(false, drawerLevel);
    }

    renderFeeds() {
        const { userStore, marketplaceFeedStore } = this.props.stores;
        const { isAdmin } = userStore;
        const {
            feeds, feedsLoaded, feedFormOpen, feedSeller, feedSaving, feedError, feedBusyId, feedConfirmDeleteId, feedEditId,
        } = this.state;
        if (!this.canManageFeeds() || !feedsLoaded) {
            return null;
        }
        if (feeds.length === 0 && !isAdmin) {
            return null;
        }
        return (
            <div class='card mt-4'>
                <div class='card-body'>
                    <div class='d-flex align-items-center mb-2'>
                        <h6 class='card-title mb-0'>
                            <i class='fa-solid fa-arrows-rotate me-2' />
                            <Text id='marketplace.feeds-title'>Automatic sources</Text>
                        </h6>
                        {isAdmin && (
                            <button type='button' class='btn btn-sm btn-outline-primary rounded-pill ms-auto' onClick={this.toggleFeedForm}>
                                <i class={`fa-solid ${feedFormOpen ? 'fa-xmark' : 'fa-plus'} me-1`} />
                                {feedFormOpen
                                    ? <Text id='marketplace.feeds-cancel'>Cancel</Text>
                                    : <Text id='marketplace.feeds-add'>Add source</Text>}
                            </button>
                        )}
                    </div>
                    <p class='small text-body-secondary mb-2'>
                        <Text id='marketplace.feeds-help'>
                            A web page with a list of items is read once a day. AI works out how to read the page the first
                            time. New rows become listings, price changes are updated, rows that disappear are marked as sold.
                            Titles, texts and pictures you edit on a listing are left alone.
                        </Text>
                    </p>

                    {feedFormOpen && isAdmin && (
                        <div class='border rounded p-2 mb-3'>
                            {feedEditId > 0 && (
                                <div class='small fw-semibold mb-1'>
                                    <i class='fa-solid fa-pen me-1' />
                                    <Text id='marketplace.feeds-editing'>Edit source</Text>
                                </div>
                            )}
                            <FormFieldsCompact
                                stores={this.props.stores}
                                sections={this.feedSections}
                                fields={['name', 'url', 'intervalHours']}
                                holdingStore={marketplaceFeedStore}
                                objectName='newMarketplaceFeed'
                                isNew={true}
                                drawerLevel={this.props.drawerLevel || 1}
                            />
                            <div class='d-flex flex-wrap align-items-center gap-2 mt-2'>
                                <span class='small text-body-secondary'>
                                    <i class='fa-solid fa-user-tag me-1' />
                                    <Text id='marketplace.feeds-seller'>Seller:</Text>
                                </span>
                                {feedSeller ? (
                                    <span class='badge text-bg-primary d-inline-flex align-items-center gap-2' style='font-size: 0.85rem;'>
                                        {userLabel(feedSeller)}
                                        <button type='button' class='btn-close btn-close-white' style='font-size: 0.6rem;' onClick={this.clearFeedSeller} />
                                    </span>
                                ) : (
                                    <div class='flex-grow-1' style='min-width: 220px; max-width: 360px;'>
                                        <SellerSearch onPick={this.pickFeedSeller} />
                                    </div>
                                )}
                                <button
                                    type='button'
                                    class='btn btn-sm btn-primary rounded-pill ms-auto'
                                    disabled={feedSaving}
                                    onClick={this.createFeed}
                                >
                                    <i class={`fa-solid ${feedSaving ? 'fa-spinner fa-spin' : 'fa-check'} me-1`} />
                                    {feedEditId > 0
                                        ? <Text id='marketplace.feeds-save-changes'>Save changes</Text>
                                        : <Text id='marketplace.feeds-save'>Save and read now</Text>}
                                </button>
                            </div>
                        </div>
                    )}

                    {feedError && <div class='alert alert-danger py-2 small'>{feedError}</div>}

                    {feeds.length === 0 ? (
                        <div class='small text-body-tertiary'>
                            <Text id='marketplace.feeds-empty'>No sources yet.</Text>
                        </div>
                    ) : (
                        <div class='list-group list-group-flush'>
                            {feeds.map(feed => {
                                const busy = feedBusyId === feed.id || !!feed.runStartedDate;
                                const summary = feed.lastSummary || {};
                                return (
                                    <div key={feed.id} class='list-group-item px-0'>
                                        <div class='d-flex flex-wrap align-items-center gap-2'>
                                            <div class='me-auto' style='min-width: 0;'>
                                                <div class='fw-semibold'>
                                                    {feed.name || feed.url}
                                                    {!feed.active && (
                                                        <span class='badge text-bg-warning ms-2'><Text id='marketplace.feeds-paused'>Paused</Text></span>
                                                    )}
                                                </div>
                                                <div class='small text-body-secondary text-truncate' style='max-width: 520px;'>
                                                    <a href={feed.url} target='_blank' rel='noopener noreferrer'>{feed.url}</a>
                                                </div>
                                                <div class='small text-body-secondary'>
                                                    <i class='fa-solid fa-user me-1' />{feed.sellerName || `#${feed.sellerUser}`}
                                                    {feed.lastRunDate && (
                                                        <>
                                                            <span class='mx-2'>·</span>
                                                            <span class={`badge ${FEED_STATUS_BADGE[feed.lastStatus] || 'text-bg-secondary'} me-1`}>
                                                                {feed.lastStatus || '-'}
                                                            </span>
                                                            {util.formatDate(feed.lastRunDate, {
                                                                hour12: false, hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short', locale: 'nb-NO',
                                                            }, true)}
                                                        </>
                                                    )}
                                                    {feed.lastStatus === 'ok' && (
                                                        <span class='ms-2'>
                                                            <Text id='marketplace.feeds-summary' fields={{ rows: summary.rows || 0, created: summary.created || 0, updated: summary.updated || 0, sold: summary.sold || 0 }}>
                                                                {summary.rows || 0} rows: {summary.created || 0} new, {summary.updated || 0} updated, {summary.sold || 0} sold
                                                            </Text>
                                                        </span>
                                                    )}
                                                    {feed.lastStatus === 'error' && feed.lastError && (
                                                        <span class='ms-2 text-danger'>{feed.lastError}</span>
                                                    )}
                                                </div>
                                                <div class='small text-body-tertiary'>
                                                    <i class='fa-solid fa-wand-magic-sparkles me-1' />
                                                    <Text id='marketplace.feeds-recipe'>Recipe:</Text>
                                                    {' '}
                                                    {feed.recipe
                                                        ? `${feed.recipe.kind === 'table' ? 'tabell' : 'kort'}${feed.recipeNotes ? `, ${feed.recipeNotes}` : ''}`
                                                        : <Text id='marketplace.feeds-recipe-none'>none yet, made by AI on the first run</Text>}
                                                    {feed.lastAnalyzeError && (
                                                        <span class='ms-2 text-danger'>{feed.lastAnalyzeError}</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div class='d-flex gap-1'>
                                                <button
                                                    type='button'
                                                    class='btn btn-sm btn-outline-primary rounded-pill'
                                                    data-id={feed.id}
                                                    disabled={busy}
                                                    onClick={this.runFeedNow}
                                                >
                                                    <i class={`fa-solid ${busy ? 'fa-spinner fa-spin' : 'fa-play'} me-1`} />
                                                    <Text id='marketplace.feeds-run'>Run now</Text>
                                                </button>
                                                {isAdmin && (
                                                    <button
                                                        type='button'
                                                        class='btn btn-sm btn-outline-secondary rounded-pill'
                                                        data-id={feed.id}
                                                        disabled={busy}
                                                        title='La AI analysere siden på nytt og kjør'
                                                        onClick={this.analyzeFeed}
                                                    >
                                                        <i class='fa-solid fa-wand-magic-sparkles me-1' />
                                                        <Text id='marketplace.feeds-analyze'>Re-analyze</Text>
                                                    </button>
                                                )}
                                                {isAdmin && (
                                                    <button
                                                        type='button'
                                                        class='btn btn-sm btn-outline-secondary rounded-pill'
                                                        data-id={feed.id}
                                                        title='Rediger kilden'
                                                        onClick={this.editFeed}
                                                    >
                                                        <i class='fa-solid fa-pen' />
                                                    </button>
                                                )}
                                                {isAdmin && (
                                                    <button
                                                        type='button'
                                                        class='btn btn-sm btn-outline-secondary rounded-pill'
                                                        data-id={feed.id}
                                                        title={feed.active ? 'Sett på pause' : 'Start igjen'}
                                                        onClick={this.toggleFeedActive}
                                                    >
                                                        <i class={`fa-solid ${feed.active ? 'fa-pause' : 'fa-play'}`} />
                                                    </button>
                                                )}
                                                {isAdmin && feedConfirmDeleteId !== feed.id && (
                                                    <button
                                                        type='button'
                                                        class='btn btn-sm btn-outline-danger rounded-pill'
                                                        data-id={feed.id}
                                                        title='Slett kilden'
                                                        onClick={this.askDeleteFeed}
                                                    >
                                                        <i class='fa-solid fa-trash' />
                                                    </button>
                                                )}
                                                {isAdmin && feedConfirmDeleteId === feed.id && (
                                                    <>
                                                        <button type='button' class='btn btn-sm btn-danger rounded-pill' data-id={feed.id} onClick={this.deleteFeed}>
                                                            <Text id='marketplace.feeds-delete-confirm'>Delete source</Text>
                                                        </button>
                                                        <button type='button' class='btn btn-sm btn-outline-secondary rounded-pill' onClick={this.cancelDeleteFeed}>
                                                            <Text id='marketplace.feeds-cancel'>Cancel</Text>
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    renderUpload() {
        const {
            loading, error, fileName, bookmarkletCopied, queue,
        } = this.state;
        return (
            <div>
                {queue.length > 0 && (
                    <div class='card border-primary mb-3'>
                        <div class='card-body py-2 d-flex flex-wrap align-items-center gap-2'>
                            <span>
                                <i class='fa-solid fa-layer-group me-2' />
                                <Text id='marketplace.finn-queue' fields={{ count: queue.length }}>Queue from Finn.no: {queue.length} ads</Text>
                            </span>
                            <span class='small text-body-secondary'>
                                <Text id='marketplace.finn-queue-help'>Run the bookmark on more Finn ads to add them. The queue is remembered in this browser.</Text>
                            </span>
                            <div class='ms-auto d-flex gap-2'>
                                <button type='button' class='btn btn-sm btn-outline-danger rounded-pill' onClick={this.clearQueue}>
                                    <i class='fa-solid fa-trash me-1' />
                                    <Text id='marketplace.finn-queue-clear'>Clear queue</Text>
                                </button>
                                <button type='button' class='btn btn-sm btn-primary rounded-pill' disabled={loading} onClick={this.previewQueue}>
                                    <i class='fa-solid fa-eye me-1' />
                                    <Text id='marketplace.finn-queue-preview'>Preview</Text>
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                <p class='text-body-secondary'>
                    <Text id='marketplace.import-intro'>
                        Upload an Excel or CSV file with your listings. The columns are recognised automatically:
                        item number, title, description, price, category, image links, Finn link and sold.
                        Rows with an item number you have imported before are updated instead of duplicated.
                    </Text>
                </p>
                <div class='mb-3'>
                    <input
                        type='file'
                        class='form-control'
                        accept='.xlsx,.xls,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel'
                        disabled={loading}
                        onChange={this.handleFile}
                    />
                </div>
                {loading && (
                    <div class='text-body-secondary'>
                        <i class='fa-duotone fa-spinner fa-spin me-2' />
                        <Text id='marketplace.import-reading' fields={{ name: fileName }}>Reading {fileName}...</Text>
                    </div>
                )}
                {error && <div class='alert alert-danger mt-3'>{error}</div>}
                <p class='small text-body-secondary mt-3 mb-0'>
                    <Text id='marketplace.import-images-note'>
                        Images are fetched from links in the file, up to three per listing. Pictures embedded in Excel cells cannot be read.
                    </Text>
                </p>

                {this.renderFeeds()}

                {/* Finn.no bookmarklet: drag to the bookmarks bar, click it on a Finn page. */}
                <div class='card mt-4'>
                    <div class='card-body'>
                        <h6 class='card-title mb-2'>
                            <i class='fa-solid fa-bookmark me-2' />
                            <Text id='marketplace.finn-title'>Fetch ads from Finn.no</Text>
                        </h6>
                        <ol class='small text-body-secondary ps-3 mb-3'>
                            <li><Text id='marketplace.finn-step-1'>Drag the button below to your browser's bookmarks bar.</Text></li>
                            <li><Text id='marketplace.finn-step-2'>Open «Mine annonser», a search or a single ad on finn.no.</Text></li>
                            <li><Text id='marketplace.finn-step-3'>Click the bookmark. This page opens with the ads ready for import.</Text></li>
                        </ol>
                        <a
                            class='btn btn-outline-primary rounded-pill'
                            href={buildFinnBookmarklet(window.location.origin)}
                            draggable={true}
                            title='Dra meg til bokmerkelinjen'
                            onClick={(e) => { e.preventDefault(); }}
                        >
                            <i class='fa-solid fa-arrow-right-to-bracket me-1' />
                            Finn → Rubrikk
                        </a>
                        {/* Copy-paste fallback for browsers where dragging does not work (phones, some Safari setups). */}
                        <div class='mt-3'>
                            <label class='form-label small text-body-secondary mb-1'>
                                <Text id='marketplace.finn-copy-label'>Or copy the code and paste it as the address of a new bookmark:</Text>
                            </label>
                            <div class='input-group input-group-sm'>
                                <input
                                    type='text'
                                    class='form-control font-monospace'
                                    readOnly
                                    value={buildFinnBookmarklet(window.location.origin)}
                                    onFocus={(e) => { e.target.select(); }}
                                />
                                <button type='button' class='btn btn-outline-secondary' onClick={this.copyBookmarklet}>
                                    <i class={`fa-solid ${bookmarkletCopied ? 'fa-check' : 'fa-copy'} me-1`} />
                                    {bookmarkletCopied ? <Text id='marketplace.finn-copied'>Copied</Text> : <Text id='marketplace.finn-copy'>Copy</Text>}
                                </button>
                            </div>
                        </div>
                        <p class='small text-body-tertiary mt-3 mb-0'>
                            <Text id='marketplace.finn-note'>
                                The bookmark only reads the ads shown on the Finn page and opens this site with them. Nothing is sent to Finn.
                                On list pages the description is left empty; open a single ad to get the full text.
                            </Text>
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    renderPreview() {
        const {
            preview, selected, loading, error, fileName, seller, sellerSearch, sellerResults, sellerSearching, sellerActiveIndex, queueMode,
        } = this.state;
        const { isAdmin } = this.props.stores.userStore;
        const items = preview.items || [];
        const summary = preview.summary || {};
        const selectableCount = items.filter(isSelectable).length;
        const selectedCount = items.filter(item => isSelectable(item) && selected[item.rowNumber]).length;
        return (
            <div>
                <div class='d-flex flex-wrap align-items-center gap-2 mb-2'>
                    <span class='text-body-secondary small'><i class='fa-duotone fa-file-spreadsheet me-1' />{fileName}</span>
                    <span class='badge text-bg-success'>{summary.new || 0} <Text id='marketplace.import-status-new'>New</Text></span>
                    <span class='badge text-bg-primary'>{summary.update || 0} <Text id='marketplace.import-status-update'>Update</Text></span>
                    <span class='badge text-bg-secondary'>{summary.unchanged || 0} <Text id='marketplace.import-status-unchanged'>Unchanged</Text></span>
                    {summary.duplicateInFile > 0 && (
                        <span class='badge text-bg-warning'>{summary.duplicateInFile} <Text id='marketplace.import-status-duplicate'>Repeated in file</Text></span>
                    )}
                    {summary.invalid > 0 && (
                        <span class='badge text-bg-danger'>{summary.invalid} <Text id='marketplace.import-status-invalid'>Invalid</Text></span>
                    )}
                    <button type='button' class='btn btn-sm btn-link text-decoration-none ms-auto' onClick={this.reset}>
                        <i class='fa-solid fa-rotate-left me-1' />
                        <Text id='marketplace.import-choose-other'>Choose another file</Text>
                    </button>
                </div>

                {preview.mapping?.source === 'heuristic' && (
                    <div class='alert alert-warning py-2 small'>
                        <i class='fa-solid fa-triangle-exclamation me-1' />
                        <Text id='marketplace.import-heuristic'>AI was not available, columns were matched by their headers. Check the preview carefully.</Text>
                    </div>
                )}
                {preview.mapping?.notes && (
                    <div class='alert alert-info py-2 small'>
                        <i class='fa-solid fa-robot me-1' />
                        {preview.mapping.notes}
                    </div>
                )}
                {!preview.hasImageColumn && (
                    <div class='alert alert-secondary py-2 small'>
                        <i class='fa-solid fa-image me-1' />
                        <Text id='marketplace.import-no-images'>No image column found. Listings are imported without pictures.</Text>
                    </div>
                )}
                {preview.truncated && (
                    <div class='alert alert-warning py-2 small'>
                        <Text id='marketplace.import-truncated' fields={{ max: preview.maxRows }}>Only the first {preview.maxRows} rows are shown and imported.</Text>
                    </div>
                )}

                {/* Admin: who becomes the seller of the imported listings */}
                {isAdmin && (
                    <div class='card mb-3'>
                        <div class='card-body py-2'>
                            <div class='d-flex flex-wrap align-items-center gap-2'>
                                <span class='small text-body-secondary'>
                                    <i class='fa-solid fa-user-tag me-1' />
                                    <Text id='marketplace.import-seller'>Import as seller:</Text>
                                </span>
                                {seller ? (
                                    <span class='badge text-bg-primary d-inline-flex align-items-center gap-2' style='font-size: 0.85rem;'>
                                        {`${seller.firstname || ''} ${seller.lastname || ''}`.trim() || seller.email || `#${seller.id}`}
                                        <button
                                            type='button'
                                            class='btn-close btn-close-white'
                                            style='font-size: 0.6rem;'
                                            title='Bruk meg selv som selger'
                                            onClick={this.clearSeller}
                                        />
                                    </span>
                                ) : (
                                    <span class='badge text-bg-secondary' style='font-size: 0.85rem;'>
                                        <Text id='marketplace.import-seller-me'>Me</Text>
                                    </span>
                                )}
                                <div class='position-relative flex-grow-1' style='min-width: 220px; max-width: 360px;'>
                                    <input
                                        type='search'
                                        class='form-control form-control-sm'
                                        placeholder='Søk etter bruker (navn, e-post, telefon)'
                                        value={sellerSearch}
                                        onInput={this.handleSellerSearch}
                                        onKeyDown={this.handleSellerKeyDown}
                                    />
                                    {(sellerResults.length > 0 || sellerSearching) && (
                                        <div class='position-absolute list-group shadow w-100' style='z-index: 1050; top: 100%;'>
                                            {sellerSearching && sellerResults.length === 0 && (
                                                <div class='list-group-item small text-body-secondary'>
                                                    <i class='fa-duotone fa-spinner fa-spin me-1' /> Søker...
                                                </div>
                                            )}
                                            {sellerResults.map((candidate, index) => (
                                                <button
                                                    type='button'
                                                    key={candidate.id}
                                                    class={`list-group-item list-group-item-action py-1 ${index === sellerActiveIndex ? 'active' : ''}`}
                                                    data-id={candidate.id}
                                                    data-index={index}
                                                    onClick={this.chooseSeller}
                                                    onMouseEnter={this.hoverSeller}
                                                >
                                                    <strong class='small'>{`${candidate.firstname || ''} ${candidate.lastname || ''}`.trim() || `#${candidate.id}`}</strong>
                                                    {candidate.email && <small class='text-body-secondary ms-2'>{candidate.email}</small>}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div class='small text-body-tertiary mt-1'>
                                <Text id='marketplace.import-seller-help'>
                                    The listings are owned by this user, and earlier imports are matched against that user's listings.
                                </Text>
                            </div>
                        </div>
                    </div>
                )}

                <div class='d-flex align-items-center gap-2 mb-2'>
                    <button type='button' class='btn btn-sm btn-outline-secondary rounded-pill' onClick={this.selectAll}>
                        <Text id='marketplace.import-select-all'>Select all</Text>
                    </button>
                    <button type='button' class='btn btn-sm btn-outline-secondary rounded-pill' onClick={this.selectNone}>
                        <Text id='marketplace.import-select-none'>Select none</Text>
                    </button>
                    <span class='small text-body-secondary ms-auto'>{selectedCount} / {selectableCount}</span>
                </div>

                <div class='table-responsive' style='max-height: 50vh; overflow: auto;'>
                    <table class='table table-sm align-middle'>
                        <thead>
                            <tr>
                                <th />
                                <th>#</th>
                                <th><Text id='marketplace.import-col-image'>Image</Text></th>
                                <th><Text id='marketplace.import-col-title'>Title</Text></th>
                                <th class='text-end'><Text id='marketplace.import-col-price'>Price</Text></th>
                                <th><Text id='marketplace.import-col-category'>Category</Text></th>
                                <th><Text id='marketplace.import-col-status'>Status</Text></th>
                                {queueMode && <th />}
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(item => {
                                const badge = STATUS_BADGE[item.status] || STATUS_BADGE.invalid;
                                const selectable = isSelectable(item);
                                return (
                                    <tr key={item.rowNumber} class={selectable ? '' : 'text-body-tertiary'}>
                                        <td>
                                            {selectable && (
                                                <input
                                                    type='checkbox'
                                                    class='form-check-input'
                                                    data-row={item.rowNumber}
                                                    checked={!!selected[item.rowNumber]}
                                                    onChange={this.toggleRow}
                                                />
                                            )}
                                        </td>
                                        <td class='small text-body-secondary'>{item.rowNumber}</td>
                                        <td>
                                            {item.imageUrls?.length > 0 ? (
                                                <img
                                                    src={item.imageUrls[0]}
                                                    alt=''
                                                    style='width: 44px; height: 44px; object-fit: cover; border-radius: 6px;'
                                                />
                                            ) : (
                                                <i class='fa-duotone fa-image text-body-tertiary' />
                                            )}
                                            {item.imageUrls?.length > 1 && (
                                                <small class='ms-1 text-body-secondary'>+{item.imageUrls.length - 1}</small>
                                            )}
                                        </td>
                                        <td>
                                            <div class='fw-semibold' style='max-width: 320px;'>{item.title}</div>
                                            {item.description && (
                                                <div class='small text-body-secondary text-truncate' style='max-width: 320px;'>{item.description}</div>
                                            )}
                                            {item.sourceId && <div class='small text-body-tertiary'>ID: {item.sourceId}</div>}
                                            {item.warnings?.length > 0 && (
                                                <div class='small text-warning'>
                                                    <i class='fa-solid fa-triangle-exclamation me-1' />
                                                    {item.warnings.join(', ')}
                                                </div>
                                            )}
                                        </td>
                                        <td class='text-end text-nowrap'>
                                            {item.price !== null && item.price !== undefined ? `Kr. ${util.format(item.price, 0)}` : '–'}
                                        </td>
                                        <td class='small'>{marketplaceCategoryLabel(item.category) || <span class='text-body-tertiary'>AI</span>}</td>
                                        <td>
                                            <span class={`badge ${badge.cls}`}>{badge.label}</span>
                                            {item.isSold && (
                                                <span class='badge text-bg-dark ms-1'><Text id='article.sold'>Sold</Text></span>
                                            )}
                                        </td>
                                        {queueMode && (
                                            <td class='text-end'>
                                                <button
                                                    type='button'
                                                    class='btn btn-sm btn-link text-danger p-0'
                                                    title='Fjern fra køen'
                                                    data-row={item.rowNumber}
                                                    disabled={loading}
                                                    onClick={this.removeQueued}
                                                >
                                                    <i class='fa-solid fa-trash' />
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {error && <div class='alert alert-danger mt-3'>{error}</div>}

                <div class='d-flex justify-content-end gap-2 mt-3'>
                    <button type='button' class='btn btn-outline-secondary rounded-pill' onClick={this.close}>
                        <Text id='action.cancel'>Cancel</Text>
                    </button>
                    <button
                        type='button'
                        class='btn btn-success rounded-pill'
                        disabled={loading || selectedCount === 0}
                        onClick={this.startImport}
                    >
                        {loading ? <i class='fa-duotone fa-spinner fa-spin me-1' /> : <i class='fa-duotone fa-file-import me-1' />}
                        <Text id='marketplace.import-start' fields={{ count: selectedCount }}>Import {selectedCount} listings</Text>
                    </button>
                </div>
            </div>
        );
    }

    renderProgress() {
        const { step, total, done, error } = this.state;
        const percent = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
        const finished = step === 'done';
        return (
            <div>
                <div class='d-flex align-items-center mb-2'>
                    {finished ? (
                        <i class={`fa-solid ${done >= total ? 'fa-circle-check text-success' : 'fa-circle-exclamation text-warning'} me-2`} />
                    ) : (
                        <i class='fa-duotone fa-spinner fa-spin me-2' />
                    )}
                    <span>
                        <Text id='marketplace.import-progress' fields={{ done, total }}>{done} of {total} listings imported</Text>
                    </span>
                </div>
                <div class='progress mb-3' style='height: 10px;'>
                    <div class='progress-bar bg-success' role='progressbar' style={`width: ${percent}%;`} />
                </div>
                {finished && done < total && !error && (
                    <div class='alert alert-warning py-2 small'>
                        <Text id='marketplace.import-incomplete'>
                            Some rows are still missing. They may have failed, see the backend log, or still be processing. Reload the list in a moment.
                        </Text>
                    </div>
                )}
                {error && <div class='alert alert-danger py-2 small'>{error}</div>}
                <p class='small text-body-secondary'>
                    <Text id='marketplace.import-ai-note'>Category and tags are filled in by AI for listings where the file had none.</Text>
                </p>
                <div class='d-flex justify-content-end gap-2 mt-3'>
                    {finished && (
                        <button type='button' class='btn btn-outline-secondary rounded-pill' onClick={this.reset}>
                            <Text id='marketplace.import-another'>Import another file</Text>
                        </button>
                    )}
                    <button type='button' class='btn btn-primary rounded-pill' onClick={this.close}>
                        <Text id='marketplace.import-close'>Close</Text>
                    </button>
                </div>
            </div>
        );
    }

    render() {
        const { step } = this.state;
        return (
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100 px-4 pt-4' style='max-width: 1100px; padding-bottom: 120px;'>
                    <h5 class='mb-3'>
                        <i class='fa-duotone fa-file-import me-2' />
                        <Text id='marketplace.import-title'>Import listings from file</Text>
                    </h5>
                    {step === 'upload' && this.renderUpload()}
                    {step === 'preview' && this.renderPreview()}
                    {(step === 'importing' || step === 'done') && this.renderProgress()}
                </div>
            </div>
        );
    }
}

export default MarketplaceImportDrawer;
